import {
  Box,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { ControllerRenderProps } from 'react-hook-form';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { convertToTestIdFormat } from '../../utils/stringUtils';

type RadioInputBox = {
  id?: string;
  text: string;
  subText?: string;
  value: string;
  tooltip?: React.ReactNode;
  disabled?: boolean;
  disabledTooltip?: React.ReactNode;
};

export type { RadioInputBox };

export interface GroupRadioButtonProps {
  dataTestId: string;
  id: string;
  options: RadioInputBox[];
  field: ControllerRenderProps<any, any>;
  disabled?: boolean;
  disabledTooltip?: React.ReactNode;
  useDrawerStyles?: boolean;
}

const DEFAULT_DISABLED_TOOLTIP =
  'You cannot select a coverage limit that exceeds the length of your lease.';

const GroupRadioButton = ({
  id,
  dataTestId,
  options,
  field,
  disabled,
  disabledTooltip,
  useDrawerStyles = false,
}: GroupRadioButtonProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isItemDisabled = (itemDisabled?: boolean) => disabled || itemDisabled;
  const isItemSelected = (itemValue: string) => field.value === itemValue;

  const getDisabledTooltipText = (itemDisabledTooltip?: React.ReactNode) =>
    itemDisabledTooltip || disabledTooltip || DEFAULT_DISABLED_TOOLTIP;

  const getBackgroundColor = (itemDisabled?: boolean, itemValue?: string) => {
    if (isItemDisabled(itemDisabled)) {
      return theme.palette.neutralGrey[50];
    }
    if (isItemSelected(itemValue || '')) {
      return 'rgba(61, 177, 87, 0.10)';
    }
    return undefined;
  };

  const getBorderColor = (itemDisabled?: boolean, itemValue?: string) => {
    if (isItemDisabled(itemDisabled)) {
      return theme.palette.neutralGrey[200];
    }
    if (isItemSelected(itemValue || '')) {
      return theme.palette.primary.main;
    }
    // Drawer styles use neutralGrey[200], original uses black
    return useDrawerStyles ? theme.palette.neutralGrey[200] : 'black';
  };

  const getTextColor = (itemDisabled?: boolean) => {
    if (isItemDisabled(itemDisabled)) {
      return theme.palette.neutralGrey[400];
    }
    return useDrawerStyles ? theme.palette.neutralGrey[950] : 'text.primary';
  };

  const getSubTextColor = (itemDisabled?: boolean) => {
    if (isItemDisabled(itemDisabled)) {
      return theme.palette.neutralGrey[300];
    }
    // Drawer styles use neutralGrey[700] for subtext, original uses same as text
    return useDrawerStyles ? theme.palette.neutralGrey[700] : getTextColor(itemDisabled);
  };

  const renderItemContent = (item: RadioInputBox) => (
    <Box
      sx={{
        border: `1px solid ${getBorderColor(item.disabled, item.value)}`,
        borderRadius: '8px',
        // Drawer styles use different padding
        ...(useDrawerStyles ? { py: '12px', pl: '8px', pr: '16px' } : { p: 2 }),
        mb: useDrawerStyles ? 0 : 2,
        textAlign: 'left',
        backgroundColor: getBackgroundColor(item.disabled, item.value),
        cursor: isItemDisabled(item.disabled) ? 'pointer' : undefined,
        ':hover': {
          backgroundColor: isItemDisabled(item.disabled) ? undefined : 'rgba(19, 102, 62, 0.15)',
        },
      }}
    >
      <FormControlLabel
        sx={{
          width: '100%',
          '& .MuiFormControlLabel-label': {
            width: '100%',
          },
          // Drawer styles add height and margin
          ...(useDrawerStyles && { height: '40px', margin: 0 }),
        }}
        disabled={isItemDisabled(item.disabled)}
        id={convertToTestIdFormat(item.value)}
        value={item.value}
        control={
          <Radio
            id={'id_' + convertToTestIdFormat(item.value)}
            sx={{
              color: `${!isItemDisabled(item.disabled) && isItemSelected(item.value) ? theme.palette.green[700] : theme.palette.neutralGrey[300]} !important`,
              // Drawer styles add padding
              ...(useDrawerStyles && { padding: 1 }),
            }}
          />
        }
        label={
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            width={'100%'}
            maxWidth={'100%'}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              flexDirection="row"
              width={'100%'}
              maxWidth={'100%'}
            >
              <Typography
                // Drawer styles use body2, original uses body1
                variant={useDrawerStyles ? 'body2' : 'body1'}
                sx={{ verticalAlign: 'bottom', color: getTextColor(item.disabled) }}
              >
                {item.text}
              </Typography>
              {item.subText && (
                <Typography
                  variant={useDrawerStyles ? 'body2' : undefined}
                  sx={{ color: getSubTextColor(item.disabled) }}
                >
                  {item.subText}
                </Typography>
              )}
            </Box>
            {item.tooltip && (
              <Tooltip
                title={item.tooltip}
                placement="right"
                componentsProps={{ tooltip: { sx: { width: '220px' } } }}
                enterTouchDelay={0}
              >
                <IconButton>
                  <InfoOutlinedIcon sx={{ color: 'grey' }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        }
      />
    </Box>
  );

  const disabledTooltipStyles = {
    tooltip: {
      sx: {
        backgroundColor: theme.palette.neutralGrey[950],
        color: theme.palette.common.white,
        fontSize: '12px',
        lineHeight: '16px',
        px: 1,
        py: 0.5,
        borderRadius: '4px',
      },
    },
  };

  return (
    <RadioGroup
      data-testid={dataTestId}
      id={id}
      {...field}
      value={field.value || ''}
      aria-labelledby={`${id}Header`}
      // Drawer styles remove margin on last child
      sx={useDrawerStyles ? { display: 'flex', flexDirection: 'column', gap: '12px' } : undefined}
    >
      {options.map((item) =>
        isItemDisabled(item.disabled) ? (
          <Tooltip
            key={item.id ?? item.value}
            arrow={false}
            title={getDisabledTooltipText(item.disabledTooltip)}
            placement={isMobile ? 'top' : 'right'}
            enterTouchDelay={0}
            componentsProps={disabledTooltipStyles}
          >
            {renderItemContent(item)}
          </Tooltip>
        ) : (
          <Box key={item.id ?? item.value}>{renderItemContent(item)}</Box>
        ),
      )}
    </RadioGroup>
  );
};

export const GroupRadioButtonV2Skeleton = ({
  count = 3,
  dataTestId,
  containerSx,
  itemSx,
}: {
  count?: number;
  dataTestId?: string;
  containerSx?: SxProps<Theme>;
  itemSx?: SxProps<Theme>;
}) => (
  <Stack data-testid={dataTestId} direction="column" gap="12px" sx={containerSx}>
    {Array.from({ length: count }, (_, i) => (
      <Box
        key={i}
        sx={{
          border: '1px solid',
          borderColor: 'neutralGrey.200',
          borderRadius: '8px',
          pl: '8px',
          pr: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          height: '64px !important',
          ...itemSx,
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="center" width="40px" height="40px">
          <Skeleton variant="circular" width={20} height={20} sx={{ flexShrink: 0 }} />
        </Box>
        <Skeleton variant="text" width="40%" height={20} />
      </Box>
    ))}
  </Stack>
);

export default GroupRadioButton;
